import React, { useEffect, useState } from "react";
import axios from "axios";
import { FiArrowRight, FiBookOpen, FiRefreshCw } from "react-icons/fi";
import { Link } from "react-router-dom";
import "../../ComponentsCss/SocialGathering/SocialGathering.css";

const BLOGS_API_URL = "https://manasapi.teamerge.in/blog/all";

function formatBlogDate(date) {
  if (!date) return "";

  const parsedDate = new Date(date);
  if (Number.isNaN(parsedDate.getTime())) return "";

  return new Intl.DateTimeFormat("en", {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(parsedDate);
}

function SocialGathering() {
  const [blogs, setBlogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    let isCurrentRequest = true;

    const fetchBlogs = async () => {
      setIsLoading(true);
      setError("");

      try {
        const response = await axios.get(BLOGS_API_URL);
        const fetchedBlogs = response.data?.data;

        if (!Array.isArray(fetchedBlogs)) {
          throw new Error("The blog response did not contain a list.");
        }

        if (isCurrentRequest) {
          setBlogs(fetchedBlogs);
        }
      } catch (requestError) {
        if (isCurrentRequest) {
          console.error("Unable to load social gathering blogs:", requestError);
          setError("We couldn’t load the blogs right now. Please try again.");
        }
      } finally {
        if (isCurrentRequest) {
          setIsLoading(false);
        }
      }
    };

    fetchBlogs();

    return () => {
      isCurrentRequest = false;
    };
  }, [retryCount]);

  return (
    <main className="gathering-page">
      <header className="gathering-header">
        <div className="gathering-header-inner">
          <Link className="gathering-brand" to="/login" aria-label="VNG Group home">
            <span className="gathering-brand-mark">V</span>
            <span>
              VNG<span className="gathering-brand-light"> Group</span>
            </span>
          </Link>
          <nav className="gathering-nav" aria-label="Main navigation">
            <Link
              className="gathering-nav-link gathering-nav-link-active"
              to="/social-gathering"
              aria-current="page"
            >
              Social gathering
            </Link>
            <Link className="gathering-nav-link" to="/support">
              Support
            </Link>
          </nav>
        </div>
      </header>

      <section className="gathering-hero" aria-labelledby="gathering-title">
        <div className="gathering-hero-content">
          <span className="gathering-hero-icon" aria-hidden="true">
            <FiBookOpen />
          </span>
          <p className="gathering-eyebrow">IDEAS WORTH SHARING</p>
          <h1 id="gathering-title">Social gathering</h1>
          <p>
            Discover stories, updates, and ideas shared by our community.
          </p>
        </div>
        <span className="gathering-hero-orb gathering-hero-orb-one" aria-hidden="true" />
        <span className="gathering-hero-orb gathering-hero-orb-two" aria-hidden="true" />
      </section>

      <section className="gathering-feed" aria-labelledby="gathering-feed-title">
        <div className="gathering-feed-heading">
          <div>
            <p className="gathering-eyebrow">FROM THE COMMUNITY</p>
            <h2 id="gathering-feed-title">Latest blogs</h2>
          </div>
          {!isLoading && !error && (
            <span className="gathering-post-count">
              {blogs.length} {blogs.length === 1 ? "story" : "stories"}
            </span>
          )}
        </div>

        {isLoading ? (
          <div className="gathering-state" role="status" aria-live="polite">
            <span className="gathering-spinner" aria-hidden="true" />
            <p>Loading the latest stories…</p>
          </div>
        ) : error ? (
          <div className="gathering-state gathering-error" role="alert">
            <p>{error}</p>
            <button
              className="gathering-retry-button"
              type="button"
              onClick={() => setRetryCount((count) => count + 1)}
            >
              <FiRefreshCw aria-hidden="true" />
              Try again
            </button>
          </div>
        ) : blogs.length === 0 ? (
          <div className="gathering-state">
            <span className="gathering-empty-icon" aria-hidden="true">
              <FiBookOpen />
            </span>
            <h3>No stories yet</h3>
            <p>Check back soon for the latest posts from the community.</p>
          </div>
        ) : (
          <div className="gathering-blog-grid">
            {blogs.map((blog) => {
              const title = blog.title || "Untitled story";
              const authorName =
                blog.guestAuthor?.name || blog.author?.name || "Community";
              const publishedDate = formatBlogDate(blog.createdAt);

              return (
                <article className="gathering-blog-card" key={blog._id || title}>
                  {blog.image ? (
                    <img
                      className="gathering-blog-image"
                      src={blog.image}
                      alt=""
                      loading="lazy"
                    />
                  ) : (
                    <div className="gathering-blog-image gathering-blog-image-placeholder">
                      <FiBookOpen aria-hidden="true" />
                    </div>
                  )}
                  <div className="gathering-blog-body">
                    <div className="gathering-blog-meta">
                      <span>{authorName}</span>
                      {publishedDate && (
                        <>
                          <span className="gathering-meta-dot" aria-hidden="true" />
                          <time dateTime={blog.createdAt}>{publishedDate}</time>
                        </>
                      )}
                    </div>
                    <h3>{title}</h3>
                    <p className="gathering-blog-description">
                      {blog.description || "No description is available for this story yet."}
                    </p>
                    <span className="gathering-read-label">
                      Community story
                      <FiArrowRight aria-hidden="true" />
                    </span>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      <footer className="gathering-footer">
        <div className="gathering-footer-inner">
          <Link className="gathering-footer-brand" to="/login">
            VNG Group
          </Link>
          <span>© {new Date().getFullYear()} VNG Group. All rights reserved.</span>
          <Link to="/support">Need help?</Link>
        </div>
      </footer>
    </main>
  );
}

export default SocialGathering;
